import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { OverlayTrigger, Tooltip, Card } from "react-bootstrap";
import { XCircleIcon } from "@heroicons/react/outline";

const RcsDropMediaAndPreview = ({
  file,
  preview,
  onFileChange,
  inputId,
  accept = "image,video",
  disabled = false,
}) => {
  const [localPreview, setLocalPreview] = useState(
    preview || (file ? URL.createObjectURL(file) : null)
  );
  const [localFile, setLocalFile] = useState(file || null);

  const acceptMime = accept
    .split(",")
    .map((t) => (t === "image" ? "image/*" : t === "video" ? "video/*" : t))
    .join(",");

  useEffect(() => {
    if (file) {
      setLocalFile(file);
      if (!preview) {
        const url = URL.createObjectURL(file);
        setLocalPreview(url);
        return () => URL.revokeObjectURL(url);
      } else {
        setLocalPreview(preview);
      }
    } else {
      setLocalFile(null);
      setLocalPreview(null);
    }
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [file, preview]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      const selected = acceptedFiles[0];
      if (selected) {
        setLocalFile(selected);
        const url = URL.createObjectURL(selected);
        setLocalPreview(url);
        onFileChange(selected);
      }
    },
    [onFileChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: acceptMime,
    multiple: false,
    onDrop,
    disabled: !!localFile || disabled,
  });

  const removeFile = () => {
    setLocalFile(null);
    if (localPreview) {
      URL.revokeObjectURL(localPreview);
      setLocalPreview(null);
    }
    onFileChange(null);
  };

  let previewElement = null;
  if (localPreview && localFile) {
    if (localFile.type && localFile.type.startsWith("image/")) {
      previewElement = (
        <div
          className="position-relative d-inline-block w-100 mb-2"
          style={{ maxWidth: "100%" }}
        >
          <img
            src={localPreview}
            alt="preview"
            className="w-100"
            style={{ maxHeight: 180, objectFit: "contain" }}
          />
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip className="m-0">Remove</Tooltip>}
          >
            <Card.Link
              className="position-absolute top-0 end-0 m-1"
              style={{ zIndex: 2, cursor: "pointer" }}
              onClick={removeFile}
            >
              <XCircleIcon className="icon icon-xs text-danger" />
            </Card.Link>
          </OverlayTrigger>
        </div>
      );
    } else if (localFile.type && localFile.type.startsWith("video/")) {
      previewElement = (
        <div
          className="position-relative d-inline-block w-100 mb-2"
          style={{ maxWidth: "100%" }}
        >
          <video controls width="100%" className="" style={{ maxHeight: 180 }}>
            <source src={localPreview} type={localFile.type} />
            Your browser does not support the video tag.
          </video>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip className="m-0">Remove</Tooltip>}
          >
            <Card.Link
              className="position-absolute top-0 end-0 m-1"
              style={{ zIndex: 2, cursor: "pointer" }}
              onClick={removeFile}
            >
              <XCircleIcon className="icon icon-xs text-danger" />
            </Card.Link>
          </OverlayTrigger>
        </div>
      );
    }
  }

  return (
    <div>
      {previewElement}
      {!localFile && (
        <div
          {...getRootProps({
            className:
              "dropzone rounded d-flex align-items-center justify-content-center m-2" +
              (disabled ? " disabled" : ""),
            id: inputId,
            style: { cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1 },
          })}
        >
          <input {...getInputProps()} disabled={disabled} />
          <div className="dz-default dz-message text-center w-100">
            {disabled ? (
              <p className="dz-button mb-0 text-muted">
                Please select required options before uploading media
              </p>
            ) : isDragActive ? (
              <p className="dz-button mb-0">Drop file here ...</p>
            ) : (
              <p className="dz-button mb-0">Drag & drop or click to upload</p>
            )}
          </div>
        </div>
      )}
      {localFile && (
        <div className="d-flex align-items-center gap-2 mt-2">
          <span className="ms-2 small text-muted">{localFile.name}</span>
        </div>
      )}
      <div className="small text-muted mt-1">Allowed: image, video</div>
    </div>
  );
};

export default RcsDropMediaAndPreview;
